import React, { useState } from 'react';
import { Shield, Building2, Ticket, Settings, HardDrive, ShieldCheck, Map, Smartphone, Wallet, CheckCircle2, Zap, MonitorPlay } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

export default function OmniOpsFeatures() {
  const [activeTab, setActiveTab] = useState('nedenbiz');

  const features = {
    nedenbiz: {
      id: 'nedenbiz',
      title: 'Neden Biz? (Farklarımız)',
      icon: <Zap className="h-5 w-5" />,
      content: (
        <div className="space-y-6">
          <h3 className="text-2xl font-bold text-slate-900">SaaS ve ERP'lerden Temel Farklarımız</h3>
          <p className="text-slate-600 leading-relaxed text-lg">
            Rakipler sadece "bilet açıp teknisyene atama" işini yaparken, OmniOps® şu konularda onlardan tamamen ayrışır:
          </p>
          <ul className="space-y-4 text-slate-600">
            <li className="flex gap-3 items-start">
              <div className="mt-1 bg-sky-100 text-sky-600 rounded-full p-1"><ShieldCheck className="h-4 w-4" /></div>
              <span><strong>Veri ve Bulut Bağımsızlığı (Self-Hosted):</strong> Verileriniz başkalarının bulutunda (SaaS) değil, kendi izole sunucularınızda barınır. Dışa bağımlılık yoktur, kurumsal veri gizliliği %100 güvendedir.</span>
            </li>
            <li className="flex gap-3 items-start">
              <div className="mt-1 bg-sky-100 text-sky-600 rounded-full p-1"><ShieldCheck className="h-4 w-4" /></div>
              <span><strong>Taşeron Ticari Mahremiyeti:</strong> Taşeronların sisteme girdiği net malzeme ve işçilik fiyatları, sistemdeki yapay zeka/filtreleme katmanı sayesinde müşteriden KESİN olarak gizlenir. Müşteri sadece kâr marjlı nihai rakamı görür.</span>
            </li>
            <li className="flex gap-3 items-start">
              <div className="mt-1 bg-sky-100 text-sky-600 rounded-full p-1"><ShieldCheck className="h-4 w-4" /></div>
              <span><strong>İnsan Müdahalesiz Otonom Kriz Yönetimi:</strong> Müşteri henüz arızayı fark etmeden, ağ izleme sistemleri sunuculardaki bir kesintiyi tespit eder, entegre otomasyon motoru üzerinden 0. saniyede otonom kriz biletini açar ve operasyon merkezini uyarır.</span>
            </li>
            <li className="flex gap-3 items-start">
              <div className="mt-1 bg-sky-100 text-sky-600 rounded-full p-1"><ShieldCheck className="h-4 w-4" /></div>
              <span><strong>Saha Öncesi Uzaktan Müdahale:</strong> Arıza geldiğinde hemen sahaya adam gönderilmez. Sisteme entegre BT envanter ajanları ve uzaktan bağlantı araçları sayesinde, IT uzmanları ofislerinden çıkmadan cihazlara uzak masaüstü/SSH bağlantısı kurarak arızayı dakikalar içinde çözebilir.</span>
            </li>
            <li className="flex gap-3 items-start">
              <div className="mt-1 bg-sky-100 text-sky-600 rounded-full p-1"><ShieldCheck className="h-4 w-4" /></div>
              <span><strong>Müşteri İçi Hiyerarşi Koruması:</strong> Müşterinizin alt personellerinin açtığı her talep doğrudan işleme alınmaz. Sistem, talebi Müşteri Yöneticisine onay e-postası (güvenli link ile) olarak gönderir. Yönetici onaylamadan süreç başlamaz.</span>
            </li>
          </ul>
        </div>
      )
    },
    biletleme: {
      id: 'biletleme',
      title: 'Karşılama & Biletleme',
      icon: <Ticket className="h-5 w-5" />,
      content: (
        <div className="space-y-6">
          <h3 className="text-2xl font-bold text-slate-900">Bölüm 1: Otonom Karşılama ve Biletleme</h3>
          <p className="text-slate-600 leading-relaxed text-lg">
            Dışarıdan gelen talepleri anında yakalayan, veri kayıplarını ve çift kayıtları önleyen akıllı biletleme altyapısı.
          </p>
          <div className="bg-slate-50 rounded-xl p-6 border border-slate-100 space-y-4">
            <div>
              <h4 className="font-bold text-slate-900 mb-1 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-500" /> Misafir Yakalama & Race Condition</h4>
              <p className="text-slate-600 text-sm">Webden veya mailden gelen isimsiz talepler kaybolmaz veya çift kayıt yaratmaz. Sistem anında geçici parola üretir, çifte kilitleme (Mutex) ile hesabı oluşturur ve müşteriye "Cari Bilgilerinizi Tamamlayın" maili atar.</p>
            </div>
            <div>
              <h4 className="font-bold text-slate-900 mb-1 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-500" /> Bilet Havuzu (Triage) & Canlı Soket</h4>
              <p className="text-slate-600 text-sm">Yenilenen (F5) ekranlara son. Yeni biletler veya aciliyet değişimleri canlı WebSocket bağlantısıyla anında operasyon ekranına düşer.</p>
            </div>
          </div>
        </div>
      )
    },
    operasyon: {
      id: 'operasyon',
      title: 'Operasyon & Dispatch',
      icon: <Map className="h-5 w-5" />,
      content: (
        <div className="space-y-6">
          <h3 className="text-2xl font-bold text-slate-900">Bölüm 2: Operasyon & Canlı Dispatch</h3>
          <p className="text-slate-600 leading-relaxed text-lg">
            Sahadaki tüm hareketleri canlı izleyin ve atamaları sistemin önerdiği en uygun teknik personele yapın.
          </p>
          <div className="bg-slate-50 rounded-xl p-6 border border-slate-100 space-y-4">
            <div>
              <h4 className="font-bold text-slate-900 mb-1 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-500" /> Canlı GPS Haritası</h4>
              <p className="text-slate-600 text-sm">Teknisyenlerin "İşe Başla" ve "Formu Kapat" anlarındaki konumları harita üzerinde anlık görüntülenir. Bir sonraki arızaya en yakın teknisyen saniyeler içinde seçilir.</p>
            </div>
            <div>
              <h4 className="font-bold text-slate-900 mb-1 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-500" /> Akıllı Atama Modelleri</h4>
              <p className="text-slate-600 text-sm">Operasyon yöneticisi atama yaparken model seçer. Eğer "Keşif" seçilirse; teknisyen doğrudan onarım yapamaz. Önce maliyet çıkarmak, finans ve müşteriden "Ön Onay" beklemek zorundadır.</p>
            </div>
            <div>
              <h4 className="font-bold text-slate-900 mb-1 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-500" /> Demirbaş SLA ve Garanti Denetimi</h4>
              <p className="text-slate-600 text-sm">Arızalı cihazın bakım sözleşmesi olup olmadığı (SÖZLEŞMELİ etiketi) ve garanti durumu daha bilet aşamasındayken ekrana yansır.</p>
            </div>
          </div>
        </div>
      )
    },
    saha: {
      id: 'saha',
      title: 'Saha Teknisyen Paneli',
      icon: <Smartphone className="h-5 w-5" />,
      content: (
        <div className="space-y-6">
          <h3 className="text-2xl font-bold text-slate-900">Bölüm 3: Saha Teknisyen Paneli (Mobil PWA)</h3>
          <p className="text-slate-600 leading-relaxed text-lg">
            Teknisyenlerinizi sahada yalnız bırakmayın. Mobil cihazlardan erişilebilen ve kanıta dayalı işlem yaptıran akıllı saha arayüzü.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
              <strong className="block text-slate-900 mb-1 text-sm">Uygulama Gibi Yükleme (PWA)</strong>
              <span className="text-xs text-slate-600">Teknisyenler tarayıcıdan girdiği paneli tek tuşla (iOS/Android) telefonlarına kurar.</span>
            </div>
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
              <strong className="block text-slate-900 mb-1 text-sm">Zorunlu GPS Check-in</strong>
              <span className="text-xs text-slate-600">Teknisyen sahaya varmadan işe başlayamaz. Sistem GPS konumunu alarak doğrular.</span>
            </div>
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
              <strong className="block text-slate-900 mb-1 text-sm">QR Kod ile Cihaz Eşleştirme</strong>
              <span className="text-xs text-slate-600">Yanlış cihaza müdahaleyi engellemek için cihaz üzerindeki barkod okutularak sisteme tanıtılır.</span>
            </div>
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
              <strong className="block text-slate-900 mb-1 text-sm">Kanıta Dayalı Onarım</strong>
              <span className="text-xs text-slate-600">Teknisyen arızaya dokunmadan önce durum fotoğrafını, onarımı bitirdikten sonra sonuç fotoğrafını yüklemek zorundadır.</span>
            </div>
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
              <strong className="block text-slate-900 mb-1 text-sm">İptal / İade Gerekçesi</strong>
              <span className="text-xs text-slate-600">Teknisyen işi çözemiyorsa "İade Et" butonuyla zorunlu bir gerekçe yazarak işi operasyon havuzuna geri atar.</span>
            </div>
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
              <strong className="block text-slate-900 mb-1 text-sm">Mobil Islak İmza</strong>
              <span className="text-xs text-slate-600">Tablet veya telefon ekranından müşteri yetkilisine dokunmatik ıslak imza attırılarak süreç kapatılır.</span>
            </div>
          </div>
        </div>
      )
    },
    musteri: {
      id: 'musteri',
      title: 'Müşteri Deneyimi',
      icon: <MonitorPlay className="h-5 w-5" />,
      content: (
        <div className="space-y-6">
          <h3 className="text-2xl font-bold text-slate-900">Bölüm 4: Müşteri Deneyimi (B2B Portal)</h3>
          <p className="text-slate-600 leading-relaxed text-lg">
            Müşterilerinize şeffaf, güvenli ve kurumsal bir deneyim sunarak aranızdaki güven bağını güçlendirin.
          </p>
          <ul className="space-y-4 text-slate-600">
             <li className="flex gap-4 items-start p-4 bg-slate-50 rounded-xl border border-slate-100">
               <div className="bg-white p-2 rounded-lg shadow-sm"><MonitorPlay className="h-5 w-5 text-sky-600" /></div>
               <div>
                 <strong className="block text-slate-900 mb-1">Dinamik B2B Portalı</strong>
                 <span className="text-sm">Müşteri, cihazlarının "SÖZLEŞMELİ" olup olmadığını, açık taleplerini ve geçmiş servis raporlarını kendisi izler.</span>
               </div>
             </li>
             <li className="flex gap-4 items-start p-4 bg-slate-50 rounded-xl border border-slate-100">
               <div className="bg-white p-2 rounded-lg shadow-sm"><MonitorPlay className="h-5 w-5 text-sky-600" /></div>
               <div>
                 <strong className="block text-slate-900 mb-1">Modern Karekodlu (QR) Evraklar</strong>
                 <span className="text-sm">Eski tip çizgili barkodlar yerine, teklif ve servis formlarında doğrudan sistem doğrulaması sağlayan modern QR kodlar kullanılır.</span>
               </div>
             </li>
             <li className="flex gap-4 items-start p-4 bg-slate-50 rounded-xl border border-slate-100">
               <div className="bg-white p-2 rounded-lg shadow-sm"><MonitorPlay className="h-5 w-5 text-sky-600" /></div>
               <div>
                 <strong className="block text-slate-900 mb-1">PDF Gizliliği</strong>
                 <span className="text-sm">Teklifler e-posta eki (PDF) olarak atılmaz (ticari gizlilik ve sızma riski). Müşteriye sadece portala girmesi için güvenli bir inceleme linki gider.</span>
               </div>
             </li>
          </ul>
        </div>
      )
    },
    finans: {
      id: 'finans',
      title: 'Finans & İzolasyon',
      icon: <Wallet className="h-5 w-5" />,
      content: (
        <div className="space-y-6">
          <h3 className="text-2xl font-bold text-slate-900">Bölüm 5: Finans & Hakediş İzolasyonu</h3>
          <p className="text-slate-600 leading-relaxed text-lg">
            Taşeronlara ödenen ücretler ile müşterilere kesilen faturalar arasındaki ticari izolasyonu kusursuzca sağlayın.
          </p>
          <div className="bg-slate-50 rounded-xl p-6 border border-slate-100 space-y-4">
            <div>
              <h4 className="font-bold text-slate-900 mb-1 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-500" /> Pause-on-Edit Özelliği</h4>
              <p className="text-slate-600 text-sm">Canlı veri akarken (sürekli sayfa yenilenirken) finans personelinin fiyat girmesi imkansızdır. OmniOps®'ta fiyat hücresine tıklandığı an sistem yenilemeyi durdurur, veri kaybını önler.</p>
            </div>
            <div>
              <h4 className="font-bold text-slate-900 mb-1 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-500" /> Maliyet Gizleme & Kâr Marjı Ekleme</h4>
              <p className="text-slate-600 text-sm">Taşeronun keşif tutarları (örneğin Net Taşeron Yol İşçiliği) arka planda hakediş olarak tutulurken, müşteriye gidecek Proforma'ya KDV'li satış fiyatı olarak kusursuzca yansıtılır. İşçilik ve yol ücretleri müşteri gözünden maskelenir.</p>
            </div>
            <div>
              <h4 className="font-bold text-slate-900 mb-1 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-500" /> Teslimat Onayı Kontrol Noktası</h4>
              <p className="text-slate-600 text-sm">Taşeronun işi bitirmesi hemen faturalanması demek değildir. Operasyon merkezi kalite kontrolü yapıp "Teslimatı Onayla" dedikten sonra iş Finans'a aktarılır.</p>
            </div>
          </div>
        </div>
      )
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto mt-12 bg-white rounded-[2rem] border border-slate-200 shadow-2xl shadow-slate-200/50 overflow-hidden flex flex-col md:flex-row min-h-[550px]">
      {/* Sidebar Navigation */}
      <div className="w-full md:w-1/3 bg-slate-50 md:border-r border-b md:border-b-0 border-slate-200 p-4 md:p-6 flex flex-row md:flex-col gap-2 overflow-x-auto custom-scrollbar-hide">
        {Object.values(features).map((feature) => (
          <button
            key={feature.id}
            onClick={() => setActiveTab(feature.id)}
            className={`flex-shrink-0 md:flex-shrink flex items-center gap-2 md:gap-3 px-3 py-3 md:px-4 md:py-4 rounded-xl text-left transition-all font-semibold whitespace-nowrap md:whitespace-normal ${
              activeTab === feature.id
                ? 'bg-white text-sky-700 shadow-md shadow-slate-200 border border-slate-100'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            <div className={`p-2 rounded-lg ${activeTab === feature.id ? 'bg-sky-100 text-sky-600' : 'bg-slate-200 text-slate-500'}`}>
              {feature.icon}
            </div>
            <span className="text-sm md:text-base">{feature.title}</span>
          </button>
        ))}
      </div>
      
      {/* Content Area */}
      <div className="w-full md:w-2/3 p-6 sm:p-8 md:p-12 bg-white relative overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="h-full flex flex-col justify-center"
          >
            {features[activeTab as keyof typeof features].content}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
